import "./instrument";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/clerk-react";
import { dark } from "@clerk/themes";
import { PostHogProvider } from "posthog-js/react";
import * as Sentry from "@sentry/react";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Add your Clerk Publishable Key to the .env file");
}

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: "2025-11-30",
};

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  capture_performance: { web_vitals: false },
});

const queryClient = new QueryClient();

const PostHogProviderWrapper = ({ children }) => {
  if (import.meta.env.VITE_APP_ENVIRONMENT !== "production")
    return <>{children}</>;
  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={options}
    >
      {children}
    </PostHogProvider>
  );
};

const isProduction = import.meta.env.VITE_APP_ENVIRONMENT === "production";

const root = createRoot(document.getElementById("root"), {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: isProduction
    ? Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn("Uncaught error", error, errorInfo.componentStack);
      })
    : (error, errorInfo) => {
        console.warn("Uncaught error", error, errorInfo.componentStack);
      },
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: isProduction ? Sentry.reactErrorHandler() : undefined,
  // Callback called when React automatically recovers from errors.
  onRecoverableError: isProduction ? Sentry.reactErrorHandler() : undefined,
});

root.render(
  <StrictMode>
    <PostHogProviderWrapper>
      <ClerkProvider
        appearance={{
          theme: dark,
        }}
        publishableKey={PUBLISHABLE_KEY}
      >
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ClerkProvider>
    </PostHogProviderWrapper>
  </StrictMode>,
);
